import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAdministratorPermissionsService } from '@/services/administrator-permissions'
import type { AdminPermission, AdministratorPermissionsResponse } from '@/services/administrator-permissions'
import { getErrorMessage } from '@/utils/error-handler'

export const useAuthPermissionsStore = defineStore('adx-auth-permissions', () => {
  const administratorPermissionsService = useAdministratorPermissionsService()

  // === State ===
  const permissions = ref<AdminPermission[]>([])
  const isSuperAdmin = ref(false)
  const administratorId = ref<number | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastFetched = ref<Date | null>(null)

  // === Getters ===
  const isLoaded = computed(() => lastFetched.value !== null)
  const hasError = computed(() => !!error.value)

  // === Actions ===
  const fetchMyPermissions = async () => {
    loading.value = true
    error.value = null

    try {
      const data = await administratorPermissionsService.getMyPermissions()

      permissions.value = data.permissions
      isSuperAdmin.value = data.isSuperAdmin
      administratorId.value = data.administratorId
      lastFetched.value = new Date()

      return data
    } catch (err: unknown) {
      error.value = getErrorMessage(err, 'Failed to load permissions')
      console.error('Error fetching permissions:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Check if user has a specific permission
   * Super admins always return true
   */
  const hasPermission = (permission: AdminPermission): boolean => {
    if (isSuperAdmin.value) return true
    return permissions.value.includes(permission)
  }

  /**
   * Check if user has ANY of the specified permissions
   * Super admins always return true
   */
  const hasAnyPermission = (permissionList: AdminPermission[]): boolean => {
    if (isSuperAdmin.value) return true
    return permissionList.some(permission => permissions.value.includes(permission))
  }

  /**
   * Check if user has ALL of the specified permissions
   * Super admins always return true
   */
  const hasAllPermissions = (permissionList: AdminPermission[]): boolean => {
    if (isSuperAdmin.value) return true
    return permissionList.every(permission => permissions.value.includes(permission))
  }

  /**
   * Clear permissions (used on logout)
   */
  const clearPermissions = () => {
    permissions.value = []
    isSuperAdmin.value = false
    administratorId.value = null
    lastFetched.value = null
    error.value = null
  }

  /**
   * Reset store state
   */
  const reset = () => {
    clearPermissions()
    loading.value = false
  }

  return {
    // State
    permissions,
    isSuperAdmin,
    administratorId,
    loading,
    error,
    lastFetched,
    // Getters
    isLoaded,
    hasError,
    // Actions
    fetchMyPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    clearPermissions,
    reset
  }
})
